import { DataManagement } from '@Helper/DataManagement';
import { DealProjectDTO, NewDealProjectDTO } from '@Helper/Interfaces';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@Store/store';
import axios from 'axios';

const BaseURL = process.env.REACT_APP_MERGEFLO_API_URL ?? '';
const Version = process.env.REACT_APP_MERGEFLO_API_VERSION ?? '';

type PurePlayBookProps = {
  purePlayBookList: DealProjectDTO[] | undefined;
  loading: boolean;
};

const initialState: PurePlayBookProps = {
  purePlayBookList: undefined,
  loading: false,
};

type initialPostState = {
  playBook: NewDealProjectDTO;
  initialID: string;
};

export const getPurePlaybook = createAsyncThunk(
  'purePlaybook/getpurePlaybook',
  async (initialID: string, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.get(
      BaseURL + Version + 'mergeflo/dealspace/' + initialID + '/playbooks',
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const postPurePlaybook = createAsyncThunk(
  'purePlaybook/postPurePlaybook',
  async (initial: initialPostState, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.post(
      BaseURL + Version + 'mergeflo/playbook',
      initial.playBook,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const patchPurePlaybook = createAsyncThunk(
  'purePlaybook/patchPurePlaybook',
  async (initial: initialPostState, { getState }) => {
    const dataManagement = new DataManagement();
    const data = {
      name: initial.playBook.name,
      taskPrefix: initial.playBook.taskPrefix,
      functionalArea: {
        name: initial.playBook.name,
        shortName: initial.playBook.taskPrefix,
        color: initial.playBook.functionalArea?.color,
      },
    };
    const response = await axios.patch(
      BaseURL + Version + 'mergeflo/playbook/' + initial.initialID,
      data,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const deletePurePlaybook = createAsyncThunk(
  'purePlaybook/deletePurePlaybook',
  async (initialID: string, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.delete(
      BaseURL + Version + 'mergeflo/playbook/' + initialID,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const getFunctionalareatype = createAsyncThunk(
  'purePlaybook/getFunctionalareatype',
  async (_, { getState }) => {
    const dataManagement = new DataManagement();
    const response = await axios.get(
      BaseURL + Version + 'mergeflo/functionalareatype',
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + dataManagement.getData('userToken'),
        },
      }
    );

    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  }
);

export const purePlaybookSlice = createSlice({
  name: 'purePlaybookSlice',
  initialState,
  reducers: {
    postPurePlaybookList: (
      state,
      action: PayloadAction<DealProjectDTO[] | undefined>
    ) => {
      state.purePlayBookList = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPurePlaybook.pending, state => {
        state.loading = true;
      })
      .addCase(getPurePlaybook.fulfilled, (state, action) => {
        if (action.payload) {
          state.purePlayBookList = action.payload;
        }
        state.loading = false;
      })
      .addCase(postPurePlaybook.pending, state => {
        state.loading = true;
      })
      .addCase(postPurePlaybook.fulfilled, (state, action) => {
        if (action.payload) {
          console.log('new', action.payload);
          state.purePlayBookList?.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(patchPurePlaybook.pending, state => {
        state.loading = true;
      })
      .addCase(patchPurePlaybook.fulfilled, (state, action) => {
        if (action.payload) {
          console.log('update', action.payload);
          state.purePlayBookList = state.purePlayBookList?.filter(
            item => item.id !== action.payload.id
          );
          state.purePlayBookList?.push(action.payload);
        }
        state.loading = false;
      })
      .addCase(deletePurePlaybook.pending, state => {
        state.loading = true;
      })
      .addCase(deletePurePlaybook.fulfilled, (state, action) => {
        if (action.payload) {
          state.purePlayBookList = state.purePlayBookList?.filter(
            item => item.id !== action.payload.id
          );
          state.purePlayBookList?.push(action.payload);
        }
        state.loading = false;
      });
  },
});

export const { postPurePlaybookList } = purePlaybookSlice.actions;

export const getPurePlaybookList = (state: RootState) =>
  state.purePlaybookReducer.purePlayBookList;

export default purePlaybookSlice.reducer;
